import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { DeviceService } from '../../device.service';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from '../../../site-dashboard.service';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import moment from 'moment';
import * as _ from 'underscore';

@Component({
	selector: 'app-device-end-voltage-charts',
	templateUrl: './device-end-voltage-charts.component.html',
})
export class DeviceEndVoltageChartsComponent implements OnInit, OnChanges {

	@Input() siteUtilizationSettings: any;
	@Input() device: any;
	@Input() currentSite: any;
	@Input() deviceInstallationDate: Date;
	@Input() maxDate: Date;
	@Input() tooltips: any = {};
	@Input() deviceType: number;
	@Input() deviceConfig: any;
	@Input() studyInfo: any = {};
	@Input() useParentFilters: boolean = false;
	@Input() workingDays: string = null;
	// null leaves the existing behaviour untouched for callers that do not pass it (power study reports).
	// Only an explicit false forces the "no data to show" state for a device that is not shipped yet.
	@Input() isDeviceShipped: boolean | null = null;

	@ViewChild('endVoltageModal') endVoltageModal;
	@ViewChild('chargeEndVoltageModal') chargeEndVoltageModal;

	// chart data
	endVoltageChartData: any = [];
	chargeEndVoltageChartData: any = [];
	endVoltageChartColumnNames: any = [];
	endVoltageInuseOptions: any = {};
	endVoltageChargeOptions: any = {};

	// daily details
	endVoltageDailyDetails: any;
	chargeEndVoltageDailyDetails: any;

	// table data
	inuseEndVoltageTableData: TableData[] = [];
	chargeEndVoltageTableData: TableData[] = [];
	endVoltageAllTableItems: any = [];
	chargeEndVoltageAllTableItems: any = [];

	// date range
	@Input() endVoltageFromDate: Date;
	@Input() endVoltageToDate: Date = new Date(new Date().setDate(new Date().getDate() - 1));
	endVoltageInvalidDateRange: boolean = false;

	// chart ready flags
	endVoltageChartReady = false;
	chargeEndVoltageChartReady = false;

	// table config
	endVoltageColumnConfig: ColumnsConfig[] = [];

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		time: TimeSource.Utc,
	};
	endVoltageTableConfig: TableConfig = {
		hasExport: false,
		hasPagination: false,
		fitScreen: true,
	};

	voltageLevels: any;

	// Shared by both the in-use and charge end-voltage sections below - they use the same date range.
	get printFilters(): { label: string, value: string }[] {
		const filters: { label: string, value: string }[] = [
			{ label: 'perf_analytic.period', value: `${moment(this.endVoltageFromDate).format('MM/DD/YYYY')} - ${moment(this.endVoltageToDate).format('MM/DD/YYYY')}` }
		];

		if (this.workingDays)
			filters.push({ label: 'g.working_days', value: this.translate.instant(this.workingDays === 'workingDays' ? 'g.yes' : 'g.no') });

		return filters;
	}

	constructor(
		private deviceService: DeviceService,
		public commonService: CommonService,
		private translate: TranslateService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.initOptions();
	}

	get studyStartedToday(): boolean {
		return !!(this.studyInfo?.id && moment.unix(this.studyInfo.start_study_time).isSame(moment(), 'day'));
	}

	ngOnChanges() {
		this.voltageLevels = this.currentSite?.voltage_levels
			? this.currentSite.voltage_levels
			: this.siteDashboardService.voltageLevels;

		if (this.device && this.currentSite) {
			if (!this.useParentFilters)
				this.initDates();

			if (this.studyStartedToday) {
				this.chargeEndVoltageChartReady = false;
				this.endVoltageChartReady = false;
				return;
			}

			if (this.deviceConfig?.dailyDetails?.hasTypes?.endVoltage)
				this.getDailyDetails('endVoltage');

			if (this.deviceConfig?.dailyDetails?.hasTypes?.chargerEndVoltage)
				this.getDailyDetails('chargeEndVoltage');
		}
	}

	private initOptions() {
		this.endVoltageColumnConfig = [
			{ key: 'eventId', name: this.translate.instant('events.sequence_id'), type: 'string', hasFilter: false },
			{ key: 'time', name: this.translate.instant('events.timestamp'), type: 'date', dateFormat: 'MM/DD/YYYY HH:mm:ss', hasFilter: false },
		];
		this.commonService.applyTimeSourceTooltips(this.endVoltageColumnConfig, this.timeSourceByColumn);

		this.endVoltageChartColumnNames = [
			this.translate.instant('rt.sequence_id'),
			this.translate.instant('g.cycles'),
			{ type: 'string', role: 'style' },
		];

		this.endVoltageInuseOptions = {
			legend: { position: 'none' },
			hAxis: {
				title: this.translate.instant('event.vpc'),
				ticks: this.getEndVoltageBuckets(1.7, 2.25, 0.05),
			},
			vAxis: {
				title: this.translate.instant('fleet_reports.in_use_cycles_percent'),
				minValue: 0,
				maxValue: 100,
			},
		};

		this.endVoltageChargeOptions = {
			legend: { position: 'none' },
			hAxis: {
				title: this.translate.instant('event.vpc'),
				ticks: this.getEndVoltageBuckets(2.25, 2.75, 0.1),
			},
			vAxis: {
				title: this.translate.instant('fleet_reports.charge_cycles_percent'),
				minValue: 0,
				maxValue: 100,
			},
		};
	}

	private initDates() {
		const installationDate = this.device.config_info.installation_date > 0
			? new Date(this.device.config_info.installation_date * 1000)
			: new Date(0);

		let formLastMonth = new Date(new Date().setDate(new Date().getDate() - 30));
		if (formLastMonth < installationDate)
			formLastMonth = installationDate;

		this.endVoltageFromDate = formLastMonth;
		this.endVoltageToDate = new Date(new Date().setDate(new Date().getDate() - 1));

		if (this.maxDate && this.endVoltageToDate > this.maxDate)
			this.endVoltageToDate = this.maxDate;
	}

	endVoltageDatesChanged() {
		this.endVoltageInvalidDateRange = false;
		if (moment(this.endVoltageFromDate).unix() > moment(this.endVoltageToDate).unix())
			this.endVoltageInvalidDateRange = true;

		this.getDailyDetails('endVoltage');
		this.getDailyDetails('chargeEndVoltage');
	}

	private getDailyDetails(type: 'endVoltage' | 'chargeEndVoltage') {
		const zoneDiff = new Date().getTimezoneOffset() * -1 * 60 * 1000;
		let fromDate: any = new Date(new Date(this.endVoltageFromDate).getTime() + zoneDiff);
		let toDate: any = new Date(new Date(this.endVoltageToDate).getTime() + zoneDiff);
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		this.deviceService.getDailyDetails(this.device.mac_address, fromDate, toDate, {}, this.studyInfo?.id || 0).subscribe(data => {
			if (type === 'endVoltage') {
				this.endVoltageDailyDetails = data;
				this.endVoltageChartData = this.getEndVoltageChartData();
			} else {
				this.chargeEndVoltageDailyDetails = data;
				this.chargeEndVoltageChartData = this.getChargeEndVoltageChartData();
			}
		});
	}

	private getEndVoltageChartData() {
		const numberOfCells = this.device.config_info.number_of_cells;
		const rangeStart = 1.7;
		const rangeEnd = 2.25;
		const step = 0.05;

		const voltages: number[] = [];
		const count = Math.round((rangeEnd - rangeStart) / step);
		for (let i = 0; i <= count; i++) {
			voltages.push(+(rangeStart + i * step).toFixed(2));
		}

		const endVoltagesCounts = new Array(voltages.length).fill(0);
		const tableData: any[][] = [];

		const workingDaysOnly = this.workingDays === 'workingDays';
		const studySiteWD = this.siteUtilizationSettings?.truck_utilization_working_days || [];

		for (const day of this.endVoltageDailyDetails) {
			const data = day.end_voltage_events_distribution;
			if (!data)
				continue;

			if (workingDaysOnly) {
				const isWorkingDay = this.commonService.isWorkingDay(day.date, studySiteWD);
				if (!isWorkingDay)
					continue;
			}

			for (const endVoltageStr in data) {
				const events = data[endVoltageStr];
				const vpc = +(+endVoltageStr / numberOfCells).toFixed(2);
				const closestIndex = this.findClosestRangeValueIndex(vpc, voltages);
				endVoltagesCounts[closestIndex] += events.length;
				tableData[closestIndex] = tableData[closestIndex] || [];
				for (const event of events) {
					tableData[closestIndex].push({
						sequence_id: event.sequence_id,
						timestamp: moment(event.timestamp * 1000).utc().format('MM/DD/YYYY HH:mm:ss'),
						vpc,
					});
				}
			}
		}

		const totalEvents = endVoltagesCounts.reduce((a, b) => a + b, 0);
		const chartData = voltages.map((v, i) => [
			v,
			numberOfCells > 0 && totalEvents ? +(endVoltagesCounts[i] / totalEvents * 100).toFixed(2) : 0,
			'color: ' + this.getEndVoltageColors(v),
		]);

		this.endVoltageAllTableItems = tableData;
		this.endVoltageChartReady = true;
		return chartData;
	}

	private getChargeEndVoltageChartData() {
		const numberOfCells = this.device.config_info.number_of_cells;
		const rangeStart = 2.25;
		const rangeEnd = 2.75;
		const step = 0.1;

		const voltages: number[] = [];
		const count = Math.round((rangeEnd - rangeStart) / step);
		for (let i = 0; i <= count; i++) {
			voltages.push(+(rangeStart + i * step).toFixed(2));
		}

		const chargeEndVoltagesCounts = new Array(voltages.length).fill(0);
		const tableData: any[][] = [];

		for (const day of this.chargeEndVoltageDailyDetails) {
			const data = day.charge_end_voltage_events_distribution;
			if (!data) continue;

			for (const endVoltageStr in data) {
				const events = data[endVoltageStr];
				const vpc = +(+endVoltageStr / numberOfCells).toFixed(2);
				const closestIndex = this.findClosestRangeValueIndex(vpc, voltages);
				chargeEndVoltagesCounts[closestIndex] += events.length;
				tableData[closestIndex] = tableData[closestIndex] || [];
				for (const event of events) {
					tableData[closestIndex].push({
						sequence_id: event.sequence_id,
						timestamp: moment(event.timestamp * 1000).utc().format('MM/DD/YYYY HH:mm:ss'),
						vpc,
					});
				}
			}
		}

		const totalEvents = chargeEndVoltagesCounts.reduce((a, b) => a + b, 0);
		const chartData = voltages.map((v, i) => [
			v,
			numberOfCells > 0 && totalEvents ? +(chargeEndVoltagesCounts[i] / totalEvents * 100).toFixed(2) : 0,
			'color: ' + this.getEndVoltageColors(v),
		]);

		this.chargeEndVoltageAllTableItems = tableData;
		this.chargeEndVoltageChartReady = true;
		return chartData;
	}

	private getEndVoltageBuckets(start: number, end: number, step: number): number[] {
		const buckets: number[] = [];
		const count = Math.round((end - start) / step);
		for (let i = 0; i <= count; i++) {
			buckets.push(+((start + i * step).toFixed(2)));
		}
		return buckets;
	}

	private findClosestRangeValueIndex(input: number, range: number[]): number {
		if (input <= range[0])
			return 0;

		if (input >= range[range.length - 1])
			return range.length - 1;

		for (let i = 1; i < range.length; i++) {
			if (input <= range[i])
				return i;
		}

		return range.length - 1;
	}

	private getEndVoltageColors(value: number): string {
		const matchedLevel = this.voltageLevels?.find((level) => value >= level.min && value < level.max);
		return matchedLevel ? matchedLevel.color : (this.voltageLevels?.[0]?.color || '#dc3912');
	}

	onSelectEndVoltage(event) {
		if (!event) return;
		const tableData: TableData[] = [];
		for (const item of this.endVoltageAllTableItems[event.row]) {
			tableData.push({ eventId: { value: item.sequence_id }, time: { value: item.timestamp } });
		}
		this.inuseEndVoltageTableData = tableData;
		this.endVoltageModal.show();
	}

	onSelectChargeEndVoltage(event) {
		if (!event) return;
		const tableData: TableData[] = [];
		for (const item of this.chargeEndVoltageAllTableItems[event.row]) {
			tableData.push({ eventId: { value: item.sequence_id }, time: { value: item.timestamp } });
		}
		this.chargeEndVoltageTableData = tableData;
		this.chargeEndVoltageModal.show();
	}

	exportEndVoltage() {
		const csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device.serial_number,
		});

		const alphabits = this.commonService.alphabetCharacters;
		const columnNames = [
			this.translate.instant('events.sequence_id'),
			this.translate.instant('events.timestamp'),
			this.translate.instant('event.vpc'),
		];

		const headerObj = {};
		columnNames.forEach((column, colIdx) => { headerObj[alphabits[colIdx]] = column; });
		const colLength = Object.keys(headerObj).length;

		const dataObj: any[] = [...csvFileHeaderInfo, headerObj];
		let chartData = [];
		for (const dayItems of this.endVoltageAllTableItems) {
			if (dayItems) {
				for (const item of dayItems) {
					chartData.push([item.sequence_id, item.timestamp, item.vpc]);
				}
			}
		}
		chartData = _.sortBy(chartData, 2).reverse();
		chartData.forEach(column => {
			const vals = {};
			column.forEach((col, idx) => { if (idx < colLength) vals[alphabits[idx]] = col; });
			dataObj.push(vals);
		});

		const fileName = this.commonService.getCleanFileName(this.currentSite.name) +
			' - ' + this.device.config_info.truck_id +
			' - ' + this.deviceService.translate('site_performance.end_voltage_report', this.device.config_info);
		new AngularCsv(dataObj, fileName);
	}

	exportChargeEndVoltage() {
		const csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device.serial_number,
		});

		const alphabits = this.commonService.alphabetCharacters;
		const columnNames = [
			this.translate.instant('events.sequence_id'),
			this.translate.instant('events.timestamp'),
			this.translate.instant('event.vpc'),
		];

		const headerObj = {};
		columnNames.forEach((column, colIdx) => { headerObj[alphabits[colIdx]] = column; });
		const colLength = Object.keys(headerObj).length;

		const dataObj: any[] = [...csvFileHeaderInfo, headerObj];
		let chartData = [];
		for (const dayItems of this.chargeEndVoltageAllTableItems) {
			if (dayItems) {
				for (const item of dayItems) {
					chartData.push([item.sequence_id, item.timestamp, item.vpc]);
				}
			}
		}
		chartData = _.sortBy(chartData, 2).reverse();
		chartData.forEach(column => {
			const vals = {};
			column.forEach((col, idx) => { if (idx < colLength) vals[alphabits[idx]] = col; });
			dataObj.push(vals);
		});

		const fileName = this.commonService.getCleanFileName(this.currentSite.name) +
			' - ' + this.device.config_info.truck_id +
			' - ' + this.deviceService.translate('site_performance.charge_end_voltage_report', this.device.config_info);
		new AngularCsv(dataObj, fileName);
	}
}
